import { useEffect, useState } from 'react';
import {
  collection,
  collectionGroup,
  documentId,
  getCountFromServer,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {
  DailyAggregateSchema,
  tenantIdFromUsageDayPath,
  type DailyAggregate,
  type Period,
} from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { isReportExcludedTenant } from '@/features/tenants/testTenantFilter';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

const TAG = 'admin/usage-basic/useUsageBasic';

export type TopLabelsRow = {
  tenantId: string;
  tenantName: string;
  countryCode: string;
  totalLabels: number;
};

export type UsageBasicMetrics = {
  series: DailyAggregate[];
  tenantsByCountry: { country: string; count: number }[];
  tenantsTotal: number;
  productsTotal: number;
  topLabelsAccounts: TopLabelsRow[];
};

export type UsageBasicState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  | { kind: 'READY'; metrics: UsageBasicMetrics };

const TOP_LABELS_LIMIT = 10;

/**
 * Hook do dashboard `/dashboard/usage-basic`: série diária global, tenants por
 * país, contagem de produtos e top 10 contas por etiquetas no período.
 *
 * Origem: sem issue — commit f6969de1f, 2026-05-04 (rota /dashboard/usage-basic).
 *
 * Contrato: `period` (datas `YYYY-MM-DD`) → `UsageBasicState`, leitura
 * one-shot por mudança de período (quatro leituras em paralelo). Doc de
 * agregado fora do `DailyAggregateSchema` é pulado só com `console.warn`, e
 * erro de query vira `ERROR` só com `console.error` — nenhum dos dois chega
 * ao PostHog (achado registrado na #926).
 *
 * Usado por: `UsageBasicDashboardPage.tsx` (rota `/dashboard/usage-basic` em
 * `routes/router.tsx`; web `apps/admin`, super-admin); `TopLabelsAccountsTable`
 * importa o tipo `TopLabelsRow`.
 *
 * Arquitetura: só leitura. Duas collection-group queries, autorizadas em
 * `firebase/firestore.rules` só para `isSuperAdmin()`:
 *  - `products` (contagem sem filtro, `getCountFromServer`) → `match
 *    /{path=**}/products/{productId}`; sem filtro não precisa de índice.
 *    Conta TODO produto de todo tenant — o comentário do match nas rules diz
 *    "produtos ativos", o que este código não filtra (achado na #926);
 *  - `days` `yyyymmdd` intervalo → `match /{path=**}/days/{yyyymmdd}` +
 *    `fieldOverrides` `days.yyyymmdd` (COLLECTION_GROUP) em
 *    `firebase/firestore.indexes.json`. O CG casa também `aggregations/*` e
 *    `crm_lookup_quota/*`; `tenantIdFromUsageDayPath` descarta o que não é
 *    bucket de tenant.
 * `aggregations/daily/days` e `tenants` são leituras diretas de coleção.
 *
 * Dados (anonimizado):
 *   aggregations/daily/days/{yyyymmdd} — `DailyAggregateSchema`
 *     (`packages/types/src/aggregations.ts`): `{ "yyyymmdd": "20261001",
 *     "tz": "America/Sao_Paulo", "schemaVersion": 2, "labels":
 *     { "printedToday": 120, "generatedToday": null }, … }` — id do doc =
 *     `yyyymmdd`, por isso o intervalo é por `documentId()`.
 *   tenants/{tenantId}/billing/usage/days/{yyyymmdd}
 *     { "yyyymmdd": "20261001", "labelsPrinted": 42 }
 *   tenants/{tenantId} → `name`, `countryCode` (ausente conta como `UNKNOWN`).
 *
 * Custo por carregamento:
 *  - aggregations/daily/days range query: 1 query
 *    (até 365 docs em M12).
 *  - tenants list completa: 1 query (super-admin, OK <= alguns
 *    milhares).
 *  - products count via getCountFromServer: 1 query.
 *  - days collectionGroup do período: 1 query (até N×D docs).
 *    Limitamos pelo número total de tenants × dias do período;
 *    para 1000 tenants × 30 dias = 30k docs. Ainda sustentável
 *    em uma query única.
 *
 *  - tenants de teste (#1008): 1 `getCountFromServer` de `products` por
 *    tenant de teste presente (hoje ~10), para descontar do total.
 *
 * Tenant de TESTE (#1008, decisão do owner: o simulador sai de todos os
 * painéis) não entra em tenants por país, total de tenants, total de produtos
 * nem top 10 de etiquetas — filtro pelo cadastro (`isReportExcludedTenant`
 * só com o id: este hook não lê assinatura, então o sandbox não cadastrado
 * continua contando; aceito na #1008). A série diária vem do agregado
 * `aggregateDailyMetrics`, filtrado na Function (#1008 item 1), não aqui.
 *
 * Otimização futura: top labels pré-calculado em
 * `aggregations/daily.topLabelTenants` (ver decisão pendente
 * do sprint).
 *
 * Issues: #926 (achados), #1008 (tenant de teste fora).
 *
 * Histórico:
 *  - 2026-10-19 20:57 +0200 — (#1008) exclui tenant de teste de país, totais
 *    e top 10.
 */
export function useUsageBasic(period: Period): UsageBasicState {
  const [state, setState] = useState<UsageBasicState>({ kind: 'LOADING' });

  useEffect(() => {
    const guard = { cancelled: false };
    setState({ kind: 'LOADING' });

    const fromCompact = period.from.replace(/-/g, '');
    const toCompact = period.to.replace(/-/g, '');

    async function load(): Promise<void> {
      try {
        const [aggSnap, tenantsSnap, productsCount, daysSnap] =
          await Promise.all([
            getDocs(
              query(
                collection(firestore, 'aggregations', 'daily', 'days'),
                where(documentId(), '>=', fromCompact),
                where(documentId(), '<=', toCompact),
              ),
            ),
            getDocs(collection(firestore, 'tenants')),
            getCountFromServer(collectionGroup(firestore, 'products')),
            getDocs(
              query(
                collectionGroup(firestore, 'days'),
                where('yyyymmdd', '>=', fromCompact),
                where('yyyymmdd', '<=', toCompact),
              ),
            ),
          ]);

        // #1008: o count do CG `products` não filtra por tenant (aggregation
        // query não tem `where` por caminho); desconta os produtos de cada
        // tenant de teste presente, um count de coleção por tenant. Antes do
        // `guard` para a carga cancelada não gravar estado velho.
        const testTenantIds = tenantsSnap.docs
          .map((t) => t.id)
          .filter((id) => isReportExcludedTenant(id));
        const testProductCounts = await Promise.all(
          testTenantIds.map((id) =>
            getCountFromServer(collection(firestore, 'tenants', id, 'products')),
          ),
        );
        const testProducts = testProductCounts.reduce((acc, c) => acc + c.data().count, 0);

        if (guard.cancelled) return;

        const series: DailyAggregate[] = [];
        for (const d of aggSnap.docs) {
          const parsed = DailyAggregateSchema.safeParse(d.data());
          if (parsed.success) series.push(parsed.data);
          else console.warn(`${TAG}: agg ${d.id} ignorado`, parsed.error.issues);
        }
        series.sort((a, b) => a.yyyymmdd.localeCompare(b.yyyymmdd));

        const countryById = new Map<string, string>();
        const countryCount = new Map<string, number>();
        const nameById = new Map<string, string>();
        for (const t of tenantsSnap.docs) {
          // #1008: tenant de teste fora da contagem por país e do total.
          if (isReportExcludedTenant(t.id)) continue;
          const data = t.data() as Record<string, unknown>;
          const cc = typeof data.countryCode === 'string' ? data.countryCode : 'UNKNOWN';
          const name = typeof data.name === 'string' ? data.name : t.id;
          countryById.set(t.id, cc);
          nameById.set(t.id, name);
          countryCount.set(cc, (countryCount.get(cc) ?? 0) + 1);
        }
        const tenantsByCountry = Array.from(countryCount.entries())
          .map(([country, count]) => ({ country, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);

        const totalsByTenant = new Map<string, number>();
        for (const d of daysSnap.docs) {
          const tid = tenantIdFromUsageDayPath(d.ref.path);
          // #1008: o volume do simulador de uso não é de cliente.
          if (!tid || isReportExcludedTenant(tid)) continue;
          const n = (d.get('labelsPrinted') as number | undefined) ?? 0;
          totalsByTenant.set(tid, (totalsByTenant.get(tid) ?? 0) + n);
        }
        const topLabelsAccounts: TopLabelsRow[] = Array.from(totalsByTenant.entries())
          .map(([tenantId, totalLabels]) => ({
            tenantId,
            tenantName: nameById.get(tenantId) ?? tenantId,
            countryCode: countryById.get(tenantId) ?? '',
            totalLabels,
          }))
          .sort((a, b) => b.totalLabels - a.totalLabels)
          .slice(0, TOP_LABELS_LIMIT);

        setState({
          kind: 'READY',
          metrics: {
            series,
            tenantsByCountry,
            tenantsTotal: tenantsSnap.size - testTenantIds.length,
            productsTotal: Math.max(0, productsCount.data().count - testProducts),
            topLabelsAccounts,
          },
        });
      } catch (err) {
        if (guard.cancelled) return;
        console.error(`${TAG}: failed`, err);
        // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
        reportNonFatal('admin_use_usage_basic_failed', {
          site: 'failed',
          code: (err as { code?: string }).code ?? null,
          ...serializeError(err),
        });
        setState({
          kind: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    void load();
    return (): void => {
      guard.cancelled = true;
    };
  }, [period.from, period.to]);

  return state;
}
